/**
 * Postal Code Input Component (Top Nav)
 *
 * A thin wrapper component for the top navigation postal code/utility selector dropdown.
 * The embedded utility-selector-wizard handles all selection logic. This component only:
 * - Updates the summary display when utility partner changes
 * - Shows fallback display (postal code + country flag + currency) when no matches
 * - Handles auto-expand on eligible regions
 * - Persists a manual close for 24 hours so auto-expand does not repeat
 * - Responds to query parameters (?region=, ?utility=)
 *
 * Events listened:
 * - MysaEvents.utilityPartnerChanged: Show provider display (postal code + name)
 * - MysaEvents.utilityPartnerCleared: Reset to initial CTA state
 * - MysaEvents.postalCodeChecked: Show fallback when no matches found
 */

import { Component } from "@theme/component";
import { UtilityStorage } from "@theme/utility-storage";
import {
  MysaEvents,
  hasUtilityQueryOverride,
  hasUtilityCampaignParam,
} from "@theme/mysa-events";
import { isClickedOutside } from "@theme/utilities";
import { IPGeolocationService } from "@theme/ip-geolocation";
import { createLogger } from "@theme/logger";

const log = createLogger("PostalCodeInput");

/** @typedef {import('./utility-partner-manager').UtilityPartnerManager} UtilityPartnerManager */
/** @typedef {import('./utility-partner-manager').UtilityPartner} UtilityPartner */

/**
 * @typedef {Object} PostalCodeInputRefs
 * @property {HTMLDetailsElement} [details]
 * @property {HTMLElement} [displayWrap]
 * @property {HTMLElement} [initialDisplay]
 * @property {HTMLElement} [selectedDisplay]
 * @property {HTMLElement} [dropdown]
 * @property {HTMLElement} [postalCodeText]
 * @property {HTMLElement} [utilityNameText]
 * @property {HTMLElement} [utilityDisplay]
 * @property {HTMLElement} [fallbackDisplay]
 * @property {HTMLElement} [flagDisplay]
 * @property {HTMLElement} [currencyDisplay]
 * @property {HTMLElement} [backdrop]
 * @property {HTMLButtonElement} [restartWizardBtn]
 */

/**
 * @extends {Component<PostalCodeInputRefs>}
 */
class PostalCodeInput extends Component {
  /** @type {AbortController | null} */
  abortController = null;

  /** @type {number | null} Auto-expand timeout ID */
  #autoExpandTimeout = null;

  /** @type {number | null} Geolocation wait timeout ID */
  #geoWaitTimeout = null;

  async connectedCallback() {
    super.connectedCallback();

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    log("connectedCallback", {
      hasDisplayWrap: !!this.refs.displayWrap,
      hasPostalCodeText: !!this.refs.postalCodeText,
      hasUtilityNameText: !!this.refs.utilityNameText,
      hasUtilityDisplay: !!this.refs.utilityDisplay,
      hasFallbackDisplay: !!this.refs.fallbackDisplay,
      hasDetails: !!this.refs.details,
      hasBackdrop: !!this.refs.backdrop,
    });

    // Restore saved selection on load
    this.restoreDisplay();

    // Listen for utility partner changes (dispatched by utility-partner-manager)
    document.addEventListener(
      MysaEvents.utilityPartnerChanged,
      (e) => this.handlePartnerChanged(/** @type {CustomEvent} */ (e)),
      { signal },
    );

    // Listen for utility partner cleared
    document.addEventListener(
      MysaEvents.utilityPartnerCleared,
      () => this.clearDisplay(),
      { signal },
    );

    // Listen for postal code checked — show fallback when no provider match
    document.addEventListener(
      MysaEvents.postalCodeChecked,
      (e) => this.handlePostalCodeChecked(/** @type {CustomEvent} */ (e)),
      { signal },
    );

    // Handle toggle: persist collapse, manage backdrop, and scroll sticking
    this.refs.details?.addEventListener(
      "toggle",
      () => {
        const details = this.refs.details;
        if (!details) return;

        // Cancel any pending auto-expand on manual interaction
        this.#cancelAutoExpand();

        if (details.open) {
          this.#showBackdrop();
          this.#startScrollStick();
        } else {
          // The wizard module loads after the dropdown opens, so its close
          // handler can miss this toggle. Persist here, on the element that
          // owns auto-expand. Globe and widget clicks still open afterwards.
          this.#persistCloseDismiss();
          this.#hideBackdrop();
          this.#stopScrollStick();
        }
      },
      { signal },
    );

    // Close dropdown when the wizard emits close-requested
    this.addEventListener(
      "utility-wizard:close-requested",
      () => {
        if (this.refs.details) this.refs.details.open = false;
      },
      { signal },
    );

    // Listen for external open requests (e.g. globe icon in main navbar)
    document.addEventListener("postal-code-input:open", () => this.open(), {
      signal,
    });

    // Mobile bottom sheet: restart — reset wizard to initial state (postal entry)
    this.refs.restartWizardBtn?.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        const wizard = /** @type {HTMLElement | null} */ (
          this.querySelector("utility-selector-wizard")
        );
        wizard?.dispatchEvent(
          new CustomEvent("utility-wizard:restart-requested"),
        );
      },
      { signal },
    );

    // Open dropdown when the floating widget requests it
    document.addEventListener(
      "utility-widget:open-requested",
      () => {
        if (this.refs.details) this.refs.details.open = true;
      },
      { signal },
    );

    // Close dropdown on outside click (backdrop clicks are outside <details>,
    // so isClickedOutside handles them — no separate backdrop listener needed)
    document.addEventListener("click", (e) => this.#handleOutsideClick(e), {
      signal,
      capture: true,
    });

    // Close dropdown on Escape key
    document.addEventListener("keydown", (e) => this.#handleEscapeKey(e), {
      signal,
    });

    this.checkAutoExpand();

    log("Initialized — event listeners bound");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abortController?.abort();
    this.#cancelAutoExpand();
    this.#cancelGeoWait();
    this.#hideBackdrop();
    this.#stopScrollStick();
  }

  /**
   * Restore display from existing utility selection or stored postal code
   */
  restoreDisplay() {
    // Read directly from UtilityStorage — the manager singleton may not
    // be initialised yet when connectedCallback fires.
    const storedUtility = UtilityStorage.getUtility();
    const storedPostal = UtilityStorage.getPostalCode();

    log("restoreDisplay", {
      storedUtility,
      storedPostal: storedPostal?.value,
    });

    if (storedUtility?.name) {
      this.showProviderDisplay(storedPostal?.value || "", storedUtility.name);
      return;
    }

    // No provider selected — only show fallback when postal code was user-confirmed
    // Do not show IP-detected postal code in banner; keep default CTA until user confirms
    if (storedPostal?.value && storedPostal.source === "user-confirmed") {
      this.showFallbackDisplay(storedPostal.value);
    }
  }

  /**
   * Handle utility partner changed event
   * @param {CustomEvent} event
   */
  handlePartnerChanged(event) {
    const detail = event.detail;
    log("handlePartnerChanged", {
      hasDetail: !!detail,
      postalCode: detail?.postalCode,
      partnerName: detail?.partnerName,
    });
    if (!detail) return;

    this.showProviderDisplay(detail.postalCode, detail.partnerName);
  }

  /**
   * Handle postal code checked — update summary with postal code.
   * Shows fallback display (postal code + country flag + currency) only when
   * no provider matches are found. When matches exist, we wait for
   * handlePartnerChanged to upgrade the display with the provider name.
   * @param {CustomEvent} event
   */
  handlePostalCodeChecked(event) {
    const detail = event.detail;
    log("handlePostalCodeChecked", {
      hasDetail: !!detail,
      postalCode: detail?.postalCode,
      matchCount: detail?.matches?.length,
    });
    if (!detail?.postalCode) return;

    // Only show fallback when no providers matched — if matches exist,
    // wait for handlePartnerChanged to show the provider display
    const hasMatches = detail.matches && detail.matches.length > 0;
    if (hasMatches) return;

    this.showFallbackDisplay(detail.postalCode);
  }

  /**
   * Show the provider display (postal code + utility name)
   * @param {string} [postalCode]
   * @param {string} [utilityName]
   */
  showProviderDisplay(postalCode, utilityName) {
    log("showProviderDisplay", { postalCode, utilityName });

    if (!this.refs.displayWrap) {
      log("showProviderDisplay: ABORT — no displayWrap ref");
      return;
    }

    if (postalCode && this.refs.postalCodeText) {
      this.refs.postalCodeText.textContent = postalCode;
    }

    if (utilityName && this.refs.utilityNameText) {
      this.refs.utilityNameText.textContent = utilityName;
    }

    // Show utility, hide fallback
    if (this.refs.utilityDisplay) this.refs.utilityDisplay.hidden = false;
    if (this.refs.fallbackDisplay) this.refs.fallbackDisplay.hidden = true;

    this.refs.displayWrap.classList.add("has-selection");
    this.#syncSummaryVisibility(true);
    log("showProviderDisplay: applied has-selection class");
  }

  /**
   * Show the fallback display (postal code + country flag + currency)
   * @param {string} postalCode
   */
  showFallbackDisplay(postalCode) {
    log("showFallbackDisplay", { postalCode });

    if (!this.refs.displayWrap) {
      log("showFallbackDisplay: ABORT — no displayWrap ref");
      return;
    }

    if (this.refs.postalCodeText) {
      this.refs.postalCodeText.textContent = postalCode;
    }

    // Hide utility, show fallback
    if (this.refs.utilityDisplay) this.refs.utilityDisplay.hidden = true;
    if (this.refs.fallbackDisplay) this.refs.fallbackDisplay.hidden = false;

    this.refs.displayWrap.classList.add("has-selection");
    this.#syncSummaryVisibility(true);
    log("showFallbackDisplay: applied has-selection class");
  }

  /**
   * Clear the summary display back to initial CTA state
   */
  clearDisplay() {
    log("clearDisplay");

    if (!this.refs.displayWrap) {
      log("clearDisplay: ABORT — no displayWrap ref");
      return;
    }

    this.refs.displayWrap.classList.remove("has-selection");
    this.#syncSummaryVisibility(false);

    // Hide both sub-displays so initial CTA is restored
    if (this.refs.utilityDisplay) this.refs.utilityDisplay.hidden = true;
    if (this.refs.fallbackDisplay) this.refs.fallbackDisplay.hidden = true;
  }

  /**
   * Keep the inactive summary label out of the accessibility tree.
   * CSS also toggles display; `hidden` is what assistive technology follows.
   * @param {boolean} hasSelection
   */
  #syncSummaryVisibility(hasSelection) {
    if (this.refs.initialDisplay)
      this.refs.initialDisplay.hidden = hasSelection;
    if (this.refs.selectedDisplay)
      this.refs.selectedDisplay.hidden = !hasSelection;
  }

  /**
   * Auto-expand the dropdown if the user is in an eligible region
   */
  async checkAutoExpand() {
    if (!this.refs.details) return;
    const signal = this.abortController?.signal;

    // ?utility= is used in campaign links — bypass dismissal to honour the intent
    if (hasUtilityCampaignParam()) {
      log("checkAutoExpand: ?utility= campaign param, opening immediately");
      this.refs.details.open = true;
      return;
    }

    if (UtilityStorage.hasUtilitySelection()) return;
    if (UtilityStorage.isDismissed(1)) return;

    // ?region= is internal/testing — respects dismissal
    if (hasUtilityQueryOverride()) {
      log("checkAutoExpand: ?region= param detected, opening");
      this.refs.details.open = true;
      return;
    }

    let location = IPGeolocationService.getLocationData();

    // The hint may still be waiting on consent or the proxy. Region is the
    // auto-expand signal. A settled unknown result does not keep the bar waiting.
    // The 10s budget covers the proxy call. A privacy dialog can stay open
    // longer than that, so a timeout during the dialog does not give up.
    if (!location.region && !IPGeolocationService.hasSettled()) {
      location = await new Promise((resolve) => {
        let didResolve = false;
        const resolveOnce = () => {
          if (didResolve) return;
          didResolve = true;
          this.#cancelGeoWait();
          resolve(IPGeolocationService.getLocationData());
        };

        const consentDialogOpen = () => {
          const privacy = window.Shopify?.customerPrivacy;
          if (!privacy) return false;

          const choice = privacy.currentVisitorConsent?.()?.preferences;
          if (choice === "yes" || choice === "no") return false;
          if (
            typeof privacy.shouldShowBanner === "function" &&
            privacy.shouldShowBanner()
          ) {
            return true;
          }

          const banner = document.querySelector(
            "#shopify-pc__banner, .shopify-pc__banner__dialog",
          );
          if (!banner) return false;

          const style = window.getComputedStyle(banner);
          if (
            style.display === "none" ||
            style.visibility === "hidden" ||
            Number(style.opacity) === 0
          ) {
            return false;
          }

          const rect = banner.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        };

        const armNetworkTimeout = () => {
          if (didResolve) return;
          this.#cancelGeoWait();
          this.#geoWaitTimeout = setTimeout(() => {
            this.#geoWaitTimeout = null;
            if (didResolve || consentDialogOpen()) return;
            resolveOnce();
          }, 10_000);
        };

        armNetworkTimeout();

        document.addEventListener(
          "visitorConsentCollected",
          () => {
            if (didResolve) return;
            if (IPGeolocationService.hasSettled()) {
              resolveOnce();
              return;
            }
            armNetworkTimeout();
          },
          { once: true, signal },
        );

        // If the component disconnects mid-wait, resolve and avoid leaking timers
        signal?.addEventListener("abort", resolveOnce, { once: true });

        document.addEventListener(
          MysaEvents.locationLoaded,
          () => {
            resolveOnce();
          },
          { once: true, signal },
        );
      });
    }

    // Component disconnected while awaiting geolocation
    if (signal?.aborted) return;

    const eligibleRegions = this.#getEligibleRegions();
    const userRegion = (location.region || "").toUpperCase();

    if (!userRegion || !eligibleRegions.includes(userRegion)) return;

    const delay = this.#getAutoExpandDelay();

    this.#autoExpandTimeout = setTimeout(() => {
      this.#autoExpandTimeout = null;
      if (signal?.aborted) return;
      if (UtilityStorage.hasUtilitySelection()) return;
      if (UtilityStorage.isDismissed(1)) return;
      if (this.refs.details && !this.refs.details.open) {
        this.refs.details.open = true;
      }
    }, delay);
  }

  /**
   * Remember a manual close for one day so the next page does not auto-expand.
   * Skips when a utility is already chosen, and does not shorten a longer
   * cooldown the wizard just stored (for example 7 days after no matches).
   */
  #persistCloseDismiss() {
    if (UtilityStorage.hasUtilitySelection()) return;
    if (UtilityStorage.isDismissed(1)) return;

    UtilityStorage.setDismissed(1);
  }

  /**
   * Cancel any pending geolocation wait timeout
   */
  #cancelGeoWait() {
    if (this.#geoWaitTimeout !== null) {
      clearTimeout(this.#geoWaitTimeout);
      this.#geoWaitTimeout = null;
    }
  }

  /**
   * Cancel any pending auto-expand timeout
   */
  #cancelAutoExpand() {
    if (this.#autoExpandTimeout !== null) {
      clearTimeout(this.#autoExpandTimeout);
      this.#autoExpandTimeout = null;
    }
  }

  /**
   * Get eligible regions from the wizard's data attribute
   * @returns {string[]}
   */
  #getEligibleRegions() {
    const wizard = /** @type {HTMLElement|null} */ (
      this.querySelector("utility-selector-wizard")
    );
    const regionsStr = wizard?.dataset.eligibleRegions || "";
    return regionsStr
      .split(",")
      .map((r) => r.trim().toUpperCase())
      .filter(Boolean);
  }

  /**
   * Get auto-expand delay from the wizard's data attribute
   * @returns {number}
   */
  #getAutoExpandDelay() {
    const wizard = /** @type {HTMLElement|null} */ (
      this.querySelector("utility-selector-wizard")
    );
    return parseInt(wizard?.dataset.popupDelay || "2000", 10);
  }

  /**
   * Handle outside click — uses shared isClickedOutside utility
   * @param {MouseEvent} e
   */
  #handleOutsideClick(e) {
    if (!this.refs.details?.open) return;

    // Let the toggle handler manage clicks on the location trigger button
    const target = /** @type {Element | null} */ (
      e.composedPath?.()[0] ?? e.target
    );
    if (target?.closest("[data-postal-code-trigger]")) return;

    if (!isClickedOutside(e, this.refs.details)) return;

    this.refs.details.open = false;
  }

  /**
   * Handle Escape key — close dropdown
   * @param {KeyboardEvent} e
   */
  #handleEscapeKey(e) {
    if (!this.refs.details?.open) return;
    if (e.key !== "Escape") return;

    this.refs.details.open = false;
  }

  /**
   * Show backdrop on mobile (bottom sheet) and lock body scroll.
   * Prevents the browser from un-rendering ::details-content when the
   * trigger scrolls off-screen. Desktop uses scroll-stick without a
   * backdrop overlay.
   */
  #showBackdrop() {
    if (!this.refs.backdrop) return;
    if (window.innerWidth > 479) return;

    this.refs.backdrop.classList.add("is-visible");
    document.documentElement.classList.add("scroll-locked");
  }

  /**
   * Hide backdrop and restore body scroll.
   */
  #hideBackdrop() {
    this.refs.backdrop?.classList.remove("is-visible");
    document.documentElement.classList.remove("scroll-locked");
  }

  // --------------------------------------------------------------------------
  // Scroll stick — dropdown starts absolute (below trigger), switches to fixed
  // when it reaches the viewport top, and reverts when scrolled back up.
  // --------------------------------------------------------------------------

  /** @type {AbortController | null} Scroll listener controller */
  #scrollAbort = null;

  /** @type {AbortController | null} Resize listener controller (persists across mobile transitions) */
  #resizeAbort = null;

  /** @type {(() => void) | null} Geometry recalculation function for current scroll stick session */
  #recalculateGeometry = null;

  /**
   * Begin watching scroll to toggle the is-stuck class on the dropdown.
   * Captures the natural size and horizontal position so they stay
   * identical when switching from absolute to fixed.
   */
  #startScrollStick() {
    const dropdown = this.refs.dropdown;
    const details = this.refs.details;
    if (!dropdown || !details) return;

    // Clean up any existing scroll listener (but not resize)
    this.#stopScrollListener();

    // Set up resize listener if not already active (persists across mobile/desktop transitions)
    if (!this.#resizeAbort) {
      this.#resizeAbort = new AbortController();
      window.addEventListener("resize", () => this.#handleScrollStickResize(), {
        passive: true,
        signal: this.#resizeAbort.signal,
      });
    }

    // Mobile uses a fixed bottom sheet — no scroll sticking needed
    if (window.innerWidth <= 479) return;

    this.#scrollAbort = new AbortController();
    const signal = this.#scrollAbort.signal;

    // Mutable geometry - recalculated on resize
    let naturalWidth = 0;
    let naturalLeft = 0;
    let isStuck = false;

    /**
     * Recalculate the dropdown's natural geometry.
     * Must be called when unstuck so measurements reflect CSS layout.
     */
    const recalculateGeometry = () => {
      // Temporarily unstick to measure natural position
      const wasStuck = dropdown.classList.contains("is-stuck");
      if (wasStuck) {
        dropdown.style.width = "";
        dropdown.style.left = "";
        dropdown.style.right = "";
        dropdown.classList.remove("is-stuck");
      }

      const rect = dropdown.getBoundingClientRect();
      naturalWidth = rect.width;
      naturalLeft = rect.left;

      // Re-apply stuck state if needed
      if (wasStuck) {
        dropdown.style.width = `${naturalWidth}px`;
        dropdown.style.left = `${naturalLeft}px`;
        dropdown.style.right = "auto";
        dropdown.classList.add("is-stuck");
      }
    };

    const update = () => {
      const triggerRect = details.getBoundingClientRect();
      const shouldStick = triggerRect.bottom <= 0;

      if (shouldStick === isStuck) return;
      isStuck = shouldStick;

      if (shouldStick) {
        dropdown.style.width = `${naturalWidth}px`;
        dropdown.style.left = `${naturalLeft}px`;
        dropdown.style.right = "auto";
        dropdown.classList.add("is-stuck");
        return;
      }

      dropdown.style.width = "";
      dropdown.style.left = "";
      dropdown.style.right = "";
      dropdown.classList.remove("is-stuck");
    };

    // Store recalculateGeometry for resize handler access
    this.#recalculateGeometry = recalculateGeometry;

    // Initial geometry capture and scroll check
    recalculateGeometry();
    update();

    window.addEventListener("scroll", update, { passive: true, signal });
  }

  /**
   * Handle resize events for scroll stick feature.
   * Transitions between mobile (no stick) and desktop (stick enabled),
   * and syncs backdrop/scroll lock state accordingly.
   */
  #handleScrollStickResize() {
    const isMobile = window.innerWidth <= 479;
    const hasScrollListener = this.#scrollAbort !== null;
    const isOpen = !!this.refs.details?.open;

    // If the dropdown is open, keep the mobile backdrop overlay in sync
    // across viewport transitions (desktop → mobile and back).
    if (isOpen) {
      isMobile ? this.#showBackdrop() : this.#hideBackdrop();
    }

    if (isMobile && hasScrollListener) {
      // Transitioning to mobile: stop scroll listener, clear stuck state
      this.#stopScrollListener();
    } else if (!isMobile && !hasScrollListener) {
      // Transitioning to desktop: restart scroll stick
      this.#startScrollStick();
    } else if (!isMobile && hasScrollListener && this.#recalculateGeometry) {
      // Desktop resize: recalculate geometry
      this.#recalculateGeometry();
    }
  }

  /**
   * Stop scroll listener and reset stuck state.
   * Does not remove resize listener.
   */
  #stopScrollListener() {
    if (this.#scrollAbort) {
      this.#scrollAbort.abort();
      this.#scrollAbort = null;
    }

    this.#recalculateGeometry = null;

    const dropdown = this.refs.dropdown;
    if (!dropdown) return;

    dropdown.classList.remove("is-stuck");
    dropdown.style.width = "";
    dropdown.style.left = "";
    dropdown.style.right = "";
  }

  /**
   * Stop all scroll stick listeners including resize.
   * Called when dropdown closes.
   */
  #stopScrollStick() {
    this.#stopScrollListener();

    if (this.#resizeAbort) {
      this.#resizeAbort.abort();
      this.#resizeAbort = null;
    }
  }

  /**
   * Open the location picker dropdown.
   * Called via the postal-code-input:open custom event.
   */
  open() {
    if (this.refs.details) this.refs.details.open = true;
  }

  /**
   * Toggle the location picker dropdown open/closed.
   * Called via on:click delegation from the navbar globe button.
   * The outside-click handler skips [data-postal-code-trigger] elements
   * so this is the sole handler for globe button clicks.
   */
  toggle() {
    if (!this.refs.details) return;
    this.refs.details.open = !this.refs.details.open;
  }
}

customElements.define("postal-code-input", PostalCodeInput);
